import { datadogRum } from '@datadog/browser-rum';
import { LeadGenerationInteractionProps, LEAD_GENERATION_INTERACTION_TYPES } from './types';

export class ValidationError extends Error {
  constructor(
    public type: 'MISSING_REQUIRED_FIELD' | 'INVALID_MODULE_TYPE' | 'INVALID_CTA_TYPE' | 'INVALID_BUTTON_LOCATION',
    public field: string,
    public value?: unknown,
  ) {
    super(`Validation error: ${type} for field ${field}`);
    this.name = 'ValidationError';
  }
}

export function validateRequiredFields<T>(data: T, requiredFields: string[]): void {
  for (const field of requiredFields) {
    const value = field.includes('.')
      ? field.split('.').reduce<unknown>((obj, key) => {
          if (obj && typeof obj === 'object') {
            return (obj as Record<string, unknown>)[key];
          }
          return undefined;
        }, data)
      : (data as Record<string, unknown>)[field];

    if (value === undefined || value === null || value === '') {
      const error = new Error(`Validation warning: MISSING_REQUIRED_FIELD for field ${field}`);
      console.warn(error.message);
      datadogRum.addError(error);
      return;
    }
  }
}

export function validateAdditionalData(
  additionalData: Array<{ key: string; value: string | number | undefined }> = [],
  requiredKeys: string[],
): void {
  const keys = new Set(additionalData.map(item => item.key));
  for (const requiredKey of requiredKeys) {
    if (!keys.has(requiredKey)) {
      const error = new Error(`Validation warning: MISSING_REQUIRED_FIELD for field additional_data.${requiredKey}`);
      console.warn(error.message);
      datadogRum.addError(error);
      return;
    }
  }
}

export function validateLeadGenerationEventData(data: LeadGenerationInteractionProps): void {
  // Common validations
  const requiredFields = ['interaction_type', 'interaction_details.location', 'interaction_details.action_detail'];
  validateRequiredFields<LeadGenerationInteractionProps>(data, requiredFields);

  // Additional data validation
  switch (data.interaction_type) {
    case LEAD_GENERATION_INTERACTION_TYPES.MODULE_ELEMENT_TAPPED:
      break;
    case LEAD_GENERATION_INTERACTION_TYPES.NAVIGATION_BAR_INTERACTION: {
      const requiredAdditionalDataNav = ['action', 'level', 'screen'];
      validateAdditionalData(data.additional_data, requiredAdditionalDataNav);
      break;
    }
    case LEAD_GENERATION_INTERACTION_TYPES.NINETAILED_EXPERIENCE_VIEWED: {
      const requiredAdditionalDataNt = ['experience_name', 'experience_id', 'variant_name', 'variant_id'];
      validateAdditionalData(data.additional_data, requiredAdditionalDataNt);
      break;
    }
    case LEAD_GENERATION_INTERACTION_TYPES.PROMO_CODE_ENTERED: {
      const requiredAdditionalDataPromoCode = ['status'];
      validateAdditionalData(data.additional_data, requiredAdditionalDataPromoCode);
      break;
    }
    default: {
      const requiredAdditionalDataDefault = ['destination', 'label', 'cta_type'];
      validateAdditionalData(data.additional_data, requiredAdditionalDataDefault);
      break;
    }
  }

  // Event-specific validations
  if (data.interaction_type === LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED) {
    validateEnrollmentFields(data);
  }
}

function validateEnrollmentFields(data: LeadGenerationInteractionProps): void {
  const requiredFields = ['email_entered', 'user_alias.alias_label', 'button_location'];
  validateRequiredFields(data, requiredFields);

  // Validate email if email_entered is true
  if (data.email_entered && !data.email) {
    const error = new Error(`Validation warning: MISSING_REQUIRED_FIELD for field email.address`);
    console.warn(error.message);
    datadogRum.addError(error);
    return;
  }
}
